import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Flame, LogOut, Mail, Inbox, FileText, Layers, Loader2, Search } from "lucide-react";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/10 rounded-sm bg-[#141414] p-6 flex items-center gap-5">
      <Icon className="w-8 h-8 text-[#E8954A]" strokeWidth={1.6} />
      <div>
        <p className="font-display text-3xl font-black leading-none">{value}</p>
        <p className="text-white/55 text-sm mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (user === false) {
      navigate("/admin/login");
      return;
    }
    if (user && user.email) {
      Promise.all([api.get("/requests"), api.get("/stats")])
        .then(([r, s]) => {
          setRequests(r.data);
          setStats(s.data);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [user, navigate]);

  if (user === null || (user && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-white/40" />
      </div>
    );
  }
  if (user === false) return null;

  const filtered = requests.filter((r) => {
    const q = query.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.project_title.toLowerCase().includes(q) ||
      (r.company || "").toLowerCase().includes(q)
    );
  });

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-black/60 backdrop-blur-2xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Flame className="w-6 h-6 text-[#E8954A]" strokeWidth={2.2} />
            <span className="font-display font-extrabold tracking-tight uppercase">Control Room</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="text-white/50 text-sm hidden sm:block">{user.email}</span>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm border border-white/15 px-4 py-2 rounded-sm hover:bg-white/5 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-2">Pitch Requests</p>
        <h1 className="font-display text-4xl font-black tracking-tighter uppercase mb-10">Inbound Interest</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Inbox} label="Total Requests" value={stats?.total ?? 0} />
          <StatCard icon={FileText} label="Screenplays" value={stats?.screenplay ?? 0} />
          <StatCard icon={Layers} label="Full Pitches" value={stats?.full_pitch ?? 0} />
          <StatCard icon={Mail} label="Unique Projects" value={stats?.by_project?.length ?? 0} />
        </div>

        {/* By project breakdown */}
        {stats?.by_project?.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2.5" data-testid="by-project-breakdown">
            {stats.by_project.map((b) => (
              <span key={b.project} className="text-sm border border-white/10 bg-[#141414] rounded-sm px-4 py-2">
                <span className="text-white/60">{b.project}</span>
                <span className="text-[#E8954A] font-bold ml-2">{b.count}</span>
              </span>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-5 max-w-md">
          <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            data-testid="search-requests"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, project, company..."
            className="w-full bg-[#141414] border border-white/15 rounded-sm h-11 pl-10 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#E8954A]"
          />
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="requests-table">
              <thead>
                <tr className="bg-[#141414] text-white/50 text-xs uppercase tracking-wider">
                  <th className="text-left font-semibold px-5 py-4">Requester</th>
                  <th className="text-left font-semibold px-5 py-4">Project</th>
                  <th className="text-left font-semibold px-5 py-4">Type</th>
                  <th className="text-left font-semibold px-5 py-4 hidden md:table-cell">Company / Role</th>
                  <th className="text-left font-semibold px-5 py-4 hidden lg:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-white/40" data-testid="no-requests">
                      {requests.length === 0 ? "No requests yet. They'll appear here as industry pros reach out." : "No matches for your search."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r._id || r.id} data-testid={`request-row-${r._id || r.id}`} className="border-t border-white/8 hover:bg-white/[0.03] transition-colors">
                      <td className="px-5 py-4">
                        <p className="font-medium text-white">{r.name}</p>
                        <a href={`mailto:${r.email}`} className="text-white/50 hover:text-[#E8954A] transition-colors text-xs">{r.email}</a>
                      </td>
                      <td className="px-5 py-4 text-white/80">{r.project_title}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm ${r.request_type === "Full Pitch" ? "bg-[#E8954A]/15 text-[#E8954A]" : "bg-white/10 text-white/80"}`}>
                          {r.request_type}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-white/60 hidden md:table-cell">
                        {r.company || "—"}{r.role ? ` · ${r.role}` : ""}
                      </td>
                      <td className="px-5 py-4 text-white/50 hidden lg:table-cell whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
